import { isGraphqlConfigured, graphqlRequest } from './graphqlClient';

export { isGraphqlConfigured as isTodosApiConfigured };

export interface BackendTodo {
  id: string;
  userId: string;
  text: string;
  completed: boolean;
  priority: string;
  listId?: string;
  /** Prayer block the task is scheduled into ('dhuhr-asr' …), null when unscheduled. */
  blockKey?: string | null;
  /** Parent task id (one level deep); null/absent on top-level tasks. */
  parentId?: string | null;
  /** 'rhemn' | 'google' | 'todoist' (contract C7: a String, not an enum). */
  source?: string;
  /** 'synced' | 'pending' | 'error'. Server-owned (§7a). */
  syncState?: string;
  /** YYYY-MM-DD, provider-owned, read-only. */
  dueDate?: string | null;
  integrationAccountId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Every Todo field the client reads, in one place. */
export const TODO_FIELDS =
  'id userId text completed priority listId blockKey parentId source syncState dueDate integrationAccountId createdAt updatedAt';

/**
 * @deprecated Capped at 1,000 rows (contract C14). Never infer deletions from
 * it; use walkAllTodos for anything that reconciles.
 */
export const getTodos = async (): Promise<BackendTodo[]> => {
  const data = await graphqlRequest<{ todos: BackendTodo[] }>(`
    query Todos {
      todos { ${TODO_FIELDS} }
    }
  `);
  return data.todos || [];
};

export const upsertTodo = async (input: { id?: string; listId?: string; text: string; completed?: boolean; priority?: string; blockKey?: string | null; parentId?: string }): Promise<BackendTodo> => {
  const data = await graphqlRequest<{ upsertTodo: BackendTodo }>(`
    mutation UpsertTodo($input: UpsertTodoInput!) {
      upsertTodo(input: $input) { ${TODO_FIELDS} }
    }
  `, { input });
  return data.upsertTodo;
};

export const deleteTodo = async (id: string): Promise<boolean> => {
  const data = await graphqlRequest<{ deleteTodo: boolean }>(`
    mutation DeleteTodo($id: ID!) { deleteTodo(id: $id) }
  `, { id });
  return !!data.deleteTodo;
};

export interface TodoPage {
  items: BackendTodo[];
  /** Opaque; pass as `after`. null when hasMore is false. */
  nextCursor: string | null;
  hasMore: boolean;
}

/** One page of the complete, keyset-paginated fetch (contract C14). Newest first. */
export const getTodosPage = async (after?: string | null, limit = 500): Promise<TodoPage> => {
  const data = await graphqlRequest<{ todosPage: TodoPage }>(`
    query TodosPage($after: String, $limit: Int) {
      todosPage(after: $after, limit: $limit) { items { ${TODO_FIELDS} } nextCursor hasMore }
    }
  `, { after: after ?? null, limit });
  return data.todosPage;
};

export interface TodoWalk {
  items: BackendTodo[];
  /**
   * true only when the LAST page said hasMore:false. Only a complete walk may
   * turn "missing on the server" into "deleted" (contract §7a).
   */
  complete: boolean;
}

/** Hard stop against a server that never says hasMore:false. 200 × 500 rows. */
const MAX_PAGES = 200;

/**
 * Walk todosPage to the end. Never throws: a failed page ends the walk with
 * what arrived and `complete: false`, so the caller upserts it and deletes
 * nothing. `shouldContinue` lets the caller abandon (tab hidden, signed out).
 */
export const walkAllTodos = async (
  shouldContinue: () => boolean = () => true,
  pageSize = 500,
): Promise<TodoWalk> => {
  const items: BackendTodo[] = [];
  let after: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    if (!shouldContinue()) return { items, complete: false };
    let res: TodoPage;
    try {
      res = await getTodosPage(after, pageSize);
    } catch {
      return { items, complete: false };
    }
    items.push(...res.items);
    if (!res.hasMore) return { items, complete: true };
    if (!res.nextCursor) return { items, complete: false };
    after = res.nextCursor;
  }
  return { items, complete: false };
};

/**
 * Move a TOP-LEVEL task to another list (contract §7b). Sub-tasks move with it
 * on the server; the caller applies the same listId to them locally. Moving a
 * sub-task on its own is rejected (BAD_USER_INPUT).
 */
export const moveTodo = async (id: string, listId: string): Promise<BackendTodo> => {
  const data = await graphqlRequest<{ moveTodo: BackendTodo }>(`
    mutation MoveTodo($id: ID!, $listId: ID!) { moveTodo(id: $id, listId: $listId) { ${TODO_FIELDS} } }
  `, { id, listId });
  return data.moveTodo;
};

const todosApi = {
  isTodosApiConfigured: isGraphqlConfigured,
  getTodos,
  getTodosPage,
  walkAllTodos,
  upsertTodo,
  deleteTodo,
  moveTodo,
};
export default todosApi;
